import { useState } from "react";
import { Loader2, Megaphone, Trash2 } from "lucide-react";
import {
  PARTNER_TIERS,
  PARTNER_TIER_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  type PartnerTier,
  type PartnerType,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";
import { useAnnouncements } from "@/features/settings/use-announcements";
import { useSegments } from "@/features/settings/use-segments";
import { useCreateAnnouncement } from "@/features/settings/use-create-announcement";
import { useToggleAnnouncement } from "@/features/settings/use-toggle-announcement";
import { useDeleteAnnouncement } from "@/features/settings/use-delete-announcement";

// SPA port of /app/admin/settings/_components/AnnouncementManager.tsx.
// Reads via useAnnouncements + useSegments; writes via three Phase 6
// mutation hooks. Audience picker is partner-typed segments only — the
// notification dispatcher treats announcements as broadcasts to partners.

const NO_SEGMENT = "__none";

export function AnnouncementManager() {
  const announcementsQuery = useAnnouncements();
  const segmentsQuery = useSegments();
  const partnerSegments = (segmentsQuery.data ?? []).filter(
    (s) => s.entity_type === "partner",
  );

  const createMutation = useCreateAnnouncement();
  const toggleMutation = useToggleAnnouncement();
  const deleteMutation = useDeleteAnnouncement();

  const [body, setBody] = useState("");
  const [active, setActive] = useState(true);
  const [audienceSegmentId, setAudienceSegmentId] = useState<string>("");
  const [tierScope, setTierScope] = useState<PartnerTier[]>([]);
  const [typeScope, setTypeScope] = useState<PartnerType[]>([]);

  function reset() {
    setBody("");
    setActive(true);
    setAudienceSegmentId("");
    setTierScope([]);
    setTypeScope([]);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    createMutation.mutate(
      {
        body: body.trim(),
        active,
        starts_at: null,
        ends_at: null,
        audience_segment_id: audienceSegmentId || null,
        tier_scope: tierScope.length > 0 ? tierScope : null,
        partner_type_scope: typeScope.length > 0 ? typeScope : null,
      },
      { onSuccess: () => reset() },
    );
  }

  const pending = createMutation.isPending;
  const announcements = announcementsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="rounded-md border bg-card p-3">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What do you want to tell partners?"
          rows={3}
          maxLength={500}
          className="resize-none border-0 bg-transparent shadow-none focus-visible:ring-0"
        />

        <div className="grid gap-3 border-t pt-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Audience segment</span>
            <Select
              value={audienceSegmentId || NO_SEGMENT}
              onValueChange={(v) =>
                setAudienceSegmentId(v === NO_SEGMENT ? "" : v)
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All partners (no segment)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SEGMENT}>
                  All partners (no segment)
                </SelectItem>
                {partnerSegments.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <div className="space-y-1 text-xs">
            <span className="text-muted-foreground">Tier scope</span>
            <ChipPicker
              options={PARTNER_TIERS}
              label={PARTNER_TIER_LABEL}
              value={tierScope}
              onChange={(next) => setTierScope(next as PartnerTier[])}
            />
          </div>

          <div className="space-y-1 text-xs sm:col-span-2">
            <span className="text-muted-foreground">Partner type scope</span>
            <ChipPicker
              options={PARTNER_TYPES}
              label={PARTNER_TYPE_LABEL}
              value={typeScope}
              onChange={(next) => setTypeScope(next as PartnerType[])}
            />
          </div>
        </div>

        <div className="flex items-center justify-between border-t pt-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent"
            />
            Active immediately
          </label>
          <Button type="submit" size="sm" disabled={pending || !body.trim()}>
            {pending ? (
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            ) : (
              <Megaphone className="mr-1.5 h-3 w-3" />
            )}
            Publish
          </Button>
        </div>
      </form>

      <ul className="space-y-2">
        {announcements.length === 0 ? (
          <li className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
            {announcementsQuery.isLoading ? "Loading…" : "No announcements yet."}
          </li>
        ) : (
          announcements.map((a) => (
            <li
              key={a.id}
              className="flex items-start gap-3 rounded-md border bg-card p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm">{a.body}</p>
                <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {formatDate(a.created_at)} · {a.active ? "Active" : "Hidden"}
                </p>
                <AnnouncementScopeChips
                  segmentLabel={
                    a.audience_segment_id
                      ? partnerSegments.find(
                          (s) => s.id === a.audience_segment_id,
                        )?.name ?? "Segment"
                      : null
                  }
                  tierScope={a.tier_scope ?? null}
                  typeScope={a.partner_type_scope ?? null}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Switch
                  checked={a.active}
                  onCheckedChange={(checked) =>
                    toggleMutation.mutate({ id: a.id, active: checked })
                  }
                />
                <button
                  type="button"
                  aria-label="Delete"
                  onClick={() => deleteMutation.mutate(a.id)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function ChipPicker<T extends string>({
  options,
  label,
  value,
  onChange,
}: {
  options: readonly T[];
  label: Record<T, string>;
  value: T[];
  onChange: (next: T[]) => void;
}) {
  function toggle(v: T) {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={
              "rounded-full border px-2.5 py-0.5 text-[11px] transition " +
              (active
                ? "border-accent bg-accent/10 text-accent"
                : "border-border bg-card text-muted-foreground hover:border-foreground/20")
            }
          >
            {label[opt]}
          </button>
        );
      })}
    </div>
  );
}

function AnnouncementScopeChips({
  segmentLabel,
  tierScope,
  typeScope,
}: {
  segmentLabel: string | null;
  tierScope: string[] | null;
  typeScope: string[] | null;
}) {
  const chips: { key: string; label: string }[] = [];
  if (segmentLabel) chips.push({ key: "seg", label: `Segment: ${segmentLabel}` });
  if (tierScope && tierScope.length > 0) {
    chips.push({
      key: "tier",
      label: `Tiers: ${tierScope
        .map((t) => PARTNER_TIER_LABEL[t as PartnerTier] ?? t)
        .join(", ")}`,
    });
  }
  if (typeScope && typeScope.length > 0) {
    chips.push({
      key: "type",
      label: `Types: ${typeScope
        .map((t) => PARTNER_TYPE_LABEL[t as PartnerType] ?? t)
        .join(", ")}`,
    });
  }
  if (chips.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {chips.map((c) => (
        <Badge key={c.key} variant="outline" className="text-[10px]">
          {c.label}
        </Badge>
      ))}
    </div>
  );
}
